/**
 * Wartungsende direkt an der Softwarestandszeile der Kundenakte pflegen.
 *
 * Bis hierher ging das nur im Katalog-Reiter der Einstellungen: Wer beim
 * Erfassen eines Stands merkte, dass die Ampel „unbekannt" zeigt, musste den
 * Kunden verlassen, in die Einstellungen wechseln, die Version suchen und
 * zurücknavigieren. Diese Maske schließt den Weg genau dort, wo er auffällt.
 *
 * Zwei Dinge muss sie sichtbar machen, sonst ist sie gefährlicher als der
 * Umweg:
 *
 *  1. WELCHE EBENE. Das Wartungsende hängt entweder an der Version selbst oder
 *     wird von der Versionslinie geerbt (die Abzeichen „EIGEN"/„VON LINIE
 *     GEERBT" zeigen das an). Beides ist von hier aus setzbar, und die Wahl
 *     steht als ausdrückliche Entscheidung im Formular — nicht als Nebeneffekt.
 *  2. WIE WEIT ES REICHT. Der Katalog ist global; die Änderung trifft jedes
 *     System mit dieser Version bzw. Linie, quer über alle Kunden. Die Maske
 *     beziffert das (`affectedSystemCount`), statt nur zu warnen.
 *
 * Geschrieben wird über den VORHANDENEN Endpunkt `PUT /maintenance/lifecycles`
 * (`useUpsertLifecycle`) — derselbe Weg, den der Katalog-Reiter benutzt. Ein
 * zweiter Schreibpfad hätte zwei Orte für Audit, Herkunft (`origin`) und den
 * Import-Schutz `keepManual` bedeutet.
 */
import { Alert, Badge, Button, Group, Modal, Radio, Select, Skeleton, Stack, Text, TextInput } from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  formatMaintenanceEnd,
  fromLocalDateString,
  toLocalDateString,
} from "../../lib/date.js";
import { DATE_POPOVER_DOWNWARDS } from "../../theme.js";
import { useUpsertLifecycle, useVersions } from "../../hooks/useMaintenance.js";
import {
  affectedSystemCount,
  hasOwnEnd,
  resolveScope,
  type LifecycleLevel,
} from "./maintenanceEndScope.js";

export interface MaintenanceEndTarget {
  productId: string;
  productName: string;
  versionId: string;
  versionLabel: string;
}

export function MaintenanceEndModal({
  target,
  onClose,
}: {
  target: MaintenanceEndTarget | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  // Der Katalog kennt Linie, Herkunft des Endes und die Zahl der betroffenen
  // Systeme bereits — dieselbe Abfrage, die der Katalog-Reiter benutzt. Ein
  // eigener Endpunkt für diese Maske wäre eine zweite Wahrheit über dieselben
  // Daten gewesen.
  const { data: versions, isLoading } = useVersions(target?.productId ?? null);
  const upsertLifecycle = useUpsertLifecycle();

  const [level, setLevel] = useState<LifecycleLevel>("version");
  const [maintenanceEnd, setMaintenanceEnd] = useState<Date | null>(null);
  const [granularity, setGranularity] = useState<"tag" | "quartal">("tag");
  const [source, setSource] = useState("");
  const [error, setError] = useState<string | null>(null);

  const scope = resolveScope(versions ?? [], target?.versionId ?? "");
  const { version, line } = scope;
  const ownEnd = version ? hasOwnEnd(version) : false;

  useEffect(() => {
    if (!target || !versions) return;
    const current = resolveScope(versions, target.versionId);
    // Vorbelegung: Trägt die Version ein eigenes Ende, ist das die Ebene, die
    // der Anwender gerade sieht — sonst die Linie, denn ein Wartungsende gilt
    // in aller Regel für die ganze Linie und wird nur ausnahmsweise je Stand
    // abweichend gepflegt.
    const startLevel: LifecycleLevel =
      current.version && hasOwnEnd(current.version) ? "version" : current.line ? "line" : "version";
    setLevel(startLevel);
    setError(null);
  }, [target, versions]);

  // Die Felder folgen der GEWÄHLTEN Ebene, nicht dem Stand beim Öffnen: Wer
  // von „Linie" auf „Version" wechselt, soll sehen, was dort tatsächlich
  // gespeichert ist (oft nichts) statt der Werte der anderen Ebene.
  useEffect(() => {
    const quelle = level === "line" ? line : version;
    const eigenesEnde = quelle && hasOwnEnd(quelle) ? quelle.maintenanceEnd : null;
    // Für die Ebene „Version" ohne eigenes Ende bleibt das geerbte Datum als
    // Ausgangspunkt stehen — es ist der Wert, von dem man abweichen will.
    const vorschlag = eigenesEnde ?? (level === "version" ? (version?.maintenanceEnd ?? null) : null);
    setMaintenanceEnd(vorschlag ? fromLocalDateString(vorschlag) : null);
    setGranularity((quelle && hasOwnEnd(quelle) ? quelle.granularity : version?.granularity) ?? "tag");
    setSource((quelle && hasOwnEnd(quelle) ? quelle.source : null) ?? "");
  }, [level, line, version]);

  function handleClose() {
    setError(null);
    onClose();
  }

  async function handleSubmit() {
    if (!target) return;
    const zielVersionId = level === "line" ? line?.id : target.versionId;
    if (!zielVersionId) return;
    if (!maintenanceEnd) {
      setError(t("maintenance.card.maintenanceEnd.dateRequired"));
      return;
    }
    try {
      await upsertLifecycle.mutateAsync({
        versionId: zielVersionId,
        maintenanceEnd: toLocalDateString(maintenanceEnd),
        granularity,
        source: source.trim() || undefined,
      });
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : t("maintenance.card.saveError"));
    }
  }

  const affected = target ? affectedSystemCount(versions ?? [], level, target.versionId) : 0;

  return (
    <Modal
      opened={target !== null}
      onClose={handleClose}
      title={
        target
          ? t("maintenance.card.maintenanceEnd.title", {
              product: target.productName,
              version: target.versionLabel,
            })
          : ""
      }
      size="md"
      centered
    >
      {isLoading || !versions ? (
        <Skeleton height={220} />
      ) : (
        <Stack gap="sm">
          {/* Woher das Ende HEUTE kommt — dieselben Abzeichen wie im
              Katalog-Reiter, damit man die Maske ohne Umlernen liest. */}
          <Group gap="xs">
            <Text size="sm" c="dimmed">
              {t("maintenance.card.maintenanceEnd.current")}
            </Text>
            <Text size="sm" fw={600}>
              {formatMaintenanceEnd(version?.maintenanceEnd ?? null, version?.granularity ?? null)}
            </Text>
            {version?.maintenanceEnd !== null && version !== null && (
              <Badge color={ownEnd ? "blue" : "gray"} variant="light">
                {ownEnd
                  ? t("pages.settings.catalog.originOwn")
                  : t("pages.settings.catalog.originInherited")}
              </Badge>
            )}
          </Group>

          <Radio.Group
            label={t("maintenance.card.maintenanceEnd.levelLabel")}
            value={level}
            onChange={(value) => setLevel(value as LifecycleLevel)}
          >
            <Stack gap={4} mt={4}>
              <Radio
                value="version"
                label={t("maintenance.card.maintenanceEnd.levelVersion", {
                  version: target?.versionLabel ?? "",
                })}
              />
              {line ? (
                <Radio
                  value="line"
                  label={t("maintenance.card.maintenanceEnd.levelLine", { line: line.label })}
                />
              ) : (
                <Text size="xs" c="dimmed">
                  {t("maintenance.card.maintenanceEnd.noLine")}
                </Text>
              )}
            </Stack>
          </Radio.Group>

          {/* Die Reichweite ist der Punkt, an dem sich diese Maske von einem
              Feld in der Kundenakte unterscheidet: Sie schreibt in den
              globalen Katalog, nicht an diesen Kunden. */}
          <Alert color="orange" variant="light">
            {t("maintenance.card.maintenanceEnd.scopeWarning", { count: affected })}
          </Alert>

          <DateInput
            label={t("pages.wartung.tableMaintenanceEnd")}
            placeholder={t("common.datePlaceholder")}
            value={maintenanceEnd}
            onChange={setMaintenanceEnd}
            popoverProps={DATE_POPOVER_DOWNWARDS}
            required
          />
          <Select
            label={t("pages.settings.catalog.versionGranularity")}
            data={[
              { value: "tag", label: t("pages.settings.catalog.granularityDay") },
              { value: "quartal", label: t("pages.settings.catalog.granularityQuarter") },
            ]}
            value={granularity}
            onChange={(v) => v && setGranularity(v as "tag" | "quartal")}
            allowDeselect={false}
          />
          <TextInput
            label={t("pages.settings.catalog.versionSource")}
            value={source}
            onChange={(e) => setSource(e.currentTarget.value)}
            placeholder={t("pages.settings.catalog.versionSourcePlaceholder")}
          />

          {error && (
            <Alert color="red" title={t("common.error")}>
              {error}
            </Alert>
          )}

          <Group justify="flex-end">
            <Button variant="default" onClick={handleClose} disabled={upsertLifecycle.isPending}>
              {t("common.cancel")}
            </Button>
            <Button loading={upsertLifecycle.isPending} onClick={() => void handleSubmit()}>
              {t("common.save")}
            </Button>
          </Group>
        </Stack>
      )}
    </Modal>
  );
}
